import type { DeckMaterialKind } from '../../../deck/materials';
import { truncateMaterialLabel } from '../../../deck/materials';
import { Badge } from '../../components/Badge';
import { DECK_MATERIAL_ICONS } from './icons';

/**
 * The longest name the badge prints before an ellipsis — room for a filename
 * with its extension, not for a sentence. Longer than the tab strip's 16,
 * because a card has the width to spend and the name is the whole point.
 */
export const CARD_SOURCE_BADGE_MAX_CHARS = 24;

/**
 * The name of the material a flashcard came from, worn on the card beside
 * its state badge: `[New] [analisi1.pdf]`.
 *
 * WHICH name is the shared rule — `cardSourceMaterial`, the same reading of
 * the card the pane uses to follow it, so a card can never wear one
 * material's name and take the pane to another. This is only the chip: the
 * DS neutral badge in its OUTLINED style — the secondary-toned one, a
 * hairline on a translucent white fill, because the card it sits on is the
 * same soft grey as the neutral fill and a filled chip vanished into it — at
 * the state badge's own size (`m`), the name cut short past
 * {@link CARD_SOURCE_BADGE_MAX_CHARS} the way the tab strip cuts a tab's.
 * Both platforms draw this one; the 8px to the state badge is the host
 * row's gap.
 *
 * A NOTE wears the note glyph before its name: a document names itself by
 * its filename, while a note's title ("Note 1") reads like any other name.
 *
 * The chip is the one part of a card's header that YIELDS: it shrinks to the
 * room its row leaves, ending in an ellipsis, so a narrow card never clips it
 * or pushes the card's buttons out. The host row must bound it (a `min-w-0`
 * row that cannot grow past the card).
 */
export function CardSourceBadge({
  label,
  kind,
  testID,
}: {
  label: string;
  /** The material's kind — a note wears its glyph. */
  kind?: DeckMaterialKind;
  testID?: string;
}) {
  const isNote = kind === 'note';
  return (
    <Badge
      type={isNote ? 'icon-label' : 'label'}
      icon={isNote ? DECK_MATERIAL_ICONS.note : undefined}
      size="m"
      colour="neutral"
      style="glass"
      className="min-w-0 shrink"
      // The FULL name for assistive tech, whatever the chip had room for.
      accessibilityLabel={label}
      testID={testID}>
      {truncateMaterialLabel(label, CARD_SOURCE_BADGE_MAX_CHARS)}
    </Badge>
  );
}
(CardSourceBadge as { displayName?: string }).displayName = 'CardSourceBadge';
